import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  groupEscalationQuery,
  saveGroupEscalation,
  type GroupEscalationType,
} from '../../api/settingsEaseedeskGroupEscalation'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Group escalation settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/escalation/
 * group_escalation/index.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/escalation/group_escalation/index-1.js`:
 * alegerea din casuta se salveaza pe loc, fara buton.
 *
 * IN CE STARE INTRA un tichet escaladat INTERN catre un grup, pe fiecare tip de
 * tichet. Starile din care se alege sunt cele la care duce fluxul tipului - o
 * stare din afara fluxului n-ar putea fi atinsa.
 *
 * Ecranul nu se deschide deloc daca escaladarea interna nu e bifata, sau daca
 * niciun tip n-are escaladare pornita; mesajele sunt cele din portal.
 */
function TypeRow({ type, onSaved }: { type: GroupEscalationType; onSaved(): void }) {
  /*
   * Ce a ales omul, cat timp raspunsul e pe drum. `value` se lua din datele
   * cererii, care se schimba abia DUPA raspuns: casuta sarea inapoi la
   * "Select.." in clipa alegerii. Iar cand serverul refuza - o stare din afara
   * fluxului tipului - ramanea pe ecran alegerea refuzata. Se sterge in
   * amandoua cazurile, deci casuta arata mereu ce e chiar salvat.
   */
  const [pending, setPending] = useState<number | null>(null)

  const save = useMutation({
    mutationFn: (status: number) => saveGroupEscalation(type.type_id, status),
    onSuccess: () => {
      setPending(null)
      toast('Settings saved.', 'Success')
      onSaved()
    },
    onError: (error) => {
      setPending(null)
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
    },
  })

  return (
    <tr>
      <td className="center">
        {/*
         * `od_console.css` da fiecarui `.badge` cerneala inchisa
         * (`--od-ink-soft`, #5b6672) si macheta scrie doar fundalul, deci
         * prescurtarea se citea inchis pe culoarea tipului. In portal era
         * alba, din `.badge` al lui Bootstrap.
         */}
        <span className="badge"
          style={{ minWidth: 23, backgroundColor: type.color_identifier, color: '#fff', textAlign: 'center' }}>
          {type.short_name}
        </span>
      </td>
      <td>{type.type_name}</td>
      <td>
        {type.statuses.length === 0 ? (
          <div className="alert alert-warning" style={{ margin: 0 }}>
            <i className="fas fa-info-circle" aria-hidden="true"></i> No workflow assigned on this type.
          </div>
        ) : (
          <select
            className="form-control change-status"
            name="status"
            value={pending ?? type.status}
            disabled={save.isPending}
            aria-label={`Escalation status for ${type.type_name}`}
            onChange={(e) => {
              const status = Number(e.target.value)
              setPending(status)
              save.mutate(status)
            }}
          >
            <option value={0}>Select..</option>
            {type.statuses.map((status) => (
              <option key={status.ID} value={status.ID}>
                {status.status_name}
              </option>
            ))}
          </select>
        )}
      </td>
    </tr>
  )
}

export function GroupEscalationSettingsPage() {
  const queryClient = useQueryClient()
  const page = useQuery(groupEscalationQuery())

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'group_escalation_settings'] })
  }

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }

  /* Cele doua conditii din portal: escaladarea interna nebifata, sau niciun tip
     cu escaladare pornita. Serverul le spune pe nume, si mesajul lui se arata. */
  if (page.error instanceof ApiError && page.error.status === 400) {
    return (
      <div className="alert alert-warning">
        <i className="fas fa-info-circle" aria-hidden="true"></i> {page.error.message}
      </div>
    )
  }
  if (page.error) return <LoadFailed what="Group escalation settings" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  return (
    <div>
      <SettingsHead
        strong="Group"
        rest="escalation settings"
        subtitle="Set in which status a ticket will be when it is internal escalated to a group"
      />

      <section className="settings">
        <div className="row">
          <div className="col-md-12">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th width={100}></th>
                  <th className="a-left">Ticket type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {page.data.ticket_types.map((type) => (
                  <TypeRow key={type.type_id} type={type} onSaved={reload} />
                ))}
                {page.data.ticket_types.length === 0 && (
                  <tr>
                    <td colSpan={3}>
                      <div className="alert alert-info" style={{ marginBottom: 0 }}>
                        <i className="fas fa-info-circle" aria-hidden="true"></i> There is no ticket type with
                        escalation activated.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  )
}
